export * from "./FileProcessingTestValues";
export * from "./FileProcessingRecovery";
export type { ConnectionTestDiagnostics, ConnectionTestStep } from "./ConnectionTestDiagnostics";
import type { FileProcessingMetadata } from "./FileProcessingMetadata";
export { getFileProcessingMetadata } from "./FileProcessingMetadata";
export type { FileProcessingMetadata } from "./FileProcessingMetadata";
import { RawRuleGraph, Relationship, Release as SdkRelease } from "@imminently/decisively-core";
import type { CompiledDerivedTable } from "./DerivedTableCompile";
import type { DerivedTable } from "./DerivedTable";
import type { ConditionExpression, Context, Control, ControlType, Sidebar } from "@imminently/interview-sdk";
import type { Job } from "./JobTypes";
import { TypeToken } from "./TypeToken";
import { ReleasePayloads } from "./releaseTestCases";

export * from "./Constants";
export * from "./InferredOrder";
export * from "./Normalisation";
export * from "./RequestMapping";
export * from "./ContactMapping";
export * from "./ContactUpdate";
export * from "./CsvDateFormats";
export * from "./OrgModelMapping";
export * from "./TypeToken";
export * from "./JobTypes";
export * from "./payload";
// Explicit so the deprecated commons alias wins over the same name from the core re-export below.
export { generatePayload } from "./payload";
export * from "./FileUtils";
export * from "./CanaryTestRunner";
export * from "./Roles";
export * from "./ExportSanitization";
export * from "./FileProcessingRouting";
export * from "./DerivedTable";
export * from "./DerivedTableCompile";
export * from "./EventTypeInfo";

export * from "@imminently/decisively-core";
export {
  escapeRegExp,
  iterateNodeElements,
  findNode,
  findReleaseDocument,
  getNamespaceForDefinedIn,
  isDocumentId,
  flattenReleaseDocuments,
  sanitizeDocumentName,
  normalizeText,
  sanitizeNamespace,
  getValueAtDate,
} from "@imminently/decisively-core";

export interface Interview {
  name: string;
  id: string;
  status: "draft" | "published";
  goal: string;
  default?: boolean;
  context?: Context;
  steps: InterviewStep[];
  // stores the responses
  data?: any;
  initialData?: Record<string, unknown>;
  sidebars?: Sidebar[];
  metadata?: Record<string, unknown>;
  serverSideDynamic?: boolean;
}

export const enumTypes = {
  text: "text",
  number: "number",
  date: "date",
  datetime: "datetime",
} as const;

export type EnumType = (typeof enumTypes)[keyof typeof enumTypes];
export type EnumOption = { label: string; value: string };

export interface Enum {
  /** The enumeration name */
  id: string;
  type: EnumType;
  options: EnumOption[];
}

export type ReleaseEnvironment = "test" | "prod";

export type ReleaseAdapterStatus = "complete" | "incomplete" | "conflict" | "error";

export interface ReleaseAdapterOwnedResource {
  stepId: string;
  definitionKey: string;
  kind: string;
  resourceId?: string;
}

export interface ReleaseAdapterOwnedResources {
  resources?: ReleaseAdapterOwnedResource[];
}

export interface ReleaseAdapterState {
  format?: 2;
  id: string;
  version: string;
  enabled: boolean;
  answers?: Record<string, unknown>;
  ownedResources?: ReleaseAdapterOwnedResources;
  lastAppliedAt?: string;
  lastStatus?: ReleaseAdapterStatus;
}


export interface Release extends SdkRelease {
  enums: Enum[];
  env: ReleaseEnvironment;
  interview_data: Interview; // deprecated - stores old interview
  interviews: Interview[]; // the new subResource that will hold zero or more interviews for a release
  rule_graph: RawRuleGraph;
  derivedTables?: CompiledDerivedTable[];
  jobs?: Job[];
  notifications?: NotificationTask[];
  fileProcessing?: ReleaseFileProcessing;
  mapping?: ReleaseMapping[];
  fileProcessingMapping?: ReleaseFileProcessingMapping;
  connections?: ReleaseConnection[];
  //compilation?: ReleaseCompilation,
  payloads?: ReleasePayloads;
  canVector?: boolean;
  adapters?: ReleaseAdapterState[];
}

export interface InterviewContext {
  entity: string;
  id?: string;
  parent?: string;
}

export interface InterviewCreateData {
  name: string;
  goal: string;
  default?: boolean;
  initialData?: string;
  metadata?: string;
  aiGenerate?: boolean;
  serverSideDynamic?: boolean;
}

export interface InterviewStep {
  id: string;
  title: string;
  // TODO
  show: "relevant" | "always";
  // just do number of controls * 15 (seconds)context?  : InterviewContext;
  time_estimate: number;
  current?: boolean;
  complete?: boolean;
  visited?: boolean;
  skipped?: boolean;
  visitable?: boolean;
  special?:
    | { type: "complete" }
    | { type: "wait"; attribute: string | undefined; timeout: number }
    | { type: "autogen"; attribute: string | undefined };
  steps?: InterviewStep[];
  layout: InterviewLayout[];
  autogen?: string;
  condition?: ConditionExpression;
  context?: Context;
  progress?: true | string;
}

export interface InterviewState {
  id: string;
  // the backend will calculate the dependencies at runtime, so the runtime can monitor them and refresh the session
  dependencies?: string[];
}

export interface InterviewLayout {
  id: string;
  title?: string;
  state?: InterviewState[];
  controls: Control[];
  condition?: ConditionExpression;
  autogen?: string;
  sidebarIds?: string[] | null;
}

/**
 * @deprecated - use InterviewLayout
 */
export type Layout = InterviewLayout;

export interface FileProcessingConfig {
  enabled: boolean;
  sshKeys?: string;
  useSshKeys?: boolean;
  usePassword?: boolean;
  username?: string;
  sftpHost?: string;
  pathPrefix?: string;
  container?: string;
  basePath?: string;
  stagingPath?: string;
  processedPath?: string;
  errorPath?: string;
  scopePaths?: {
    sessions?: {
      basePath?: string;
      stagingPath?: string;
      processedPath?: string;
      errorPath?: string;
    };
    contacts?: {
      basePath?: string;
      stagingPath?: string;
      processedPath?: string;
      errorPath?: string;
    };
    entities?: {
      basePath?: string;
      stagingPath?: string;
      processedPath?: string;
      errorPath?: string;
    };
    orgmodels?: {
      basePath?: string;
      stagingPath?: string;
      processedPath?: string;
      errorPath?: string;
    };
  };
  /**
   * Hint flag from the UI indicating that credentials should be regenerated.
   * This is not persisted on the model; it is only used by the provisioning hook.
   */
  regenerateCredentials?: boolean;
}

export interface Model {
  id: string;
  name: string;
  locked?: boolean;
  lockedBy?: string;
  fileProcessing?: FileProcessingConfig;
  /**
   * When true, the compiler derives rule node ids from normalized descriptions instead of
   * minting random GUIDs. Set once at project creation (see the create-project modal); not
   * retro-applied to ids already minted on existing releases.
   */
  disableGuids?: boolean;
}

export interface FileProcessingFileInfo {
  processing?: FileProcessingMetadata;
  fileId: string;
  name: string;
  path?: string;
  scope?: "sessions" | "contacts" | "entities" | "orgmodels";
  status?: "staging" | "processed" | "error";
  size: number;
  lastModified: string;
  contentType?: string;
}

export interface FileTokenResponse {
  uploadUrl: string;
  baseUrl: string;
  token: string;
  expiresAt: string;
  pathPrefix: string;
  sftpHost: string;
}

export type FileProcessingScope = "sessions" | "contacts" | "entities" | "orgmodels";

export type FileProcessingTestStatus = "queued" | "processing" | "completed" | "failed";

export type FileProcessingTestMilestoneType =
  | "uploaded"
  | "detected"
  | "release-resolved"
  | "routing-resolved"
  | "file-read"
  | "mapping-completed"
  | "decision-api-started"
  | "decision-api-completed"
  | "rows-failed"
  | "records-written"
  | "file-completed"
  | "failed";

export interface FileProcessingTestMilestone {
  id: string;
  type: FileProcessingTestMilestoneType;
  status: "pending" | "success" | "error";
  message: string;
  timestamp: string;
  callId?: string;
  details?: unknown;
}

export type FileProcessingTestErrorCategory =
  | "input-data"
  | "mapping-configuration"
  | "mapping-or-input"
  | "validation"
  | "service"
  | "unknown";

export interface FileProcessingTestErrorDetail {
  message: string;
  description?: string;
  category?: FileProcessingTestErrorCategory;
  stage?: string;
  mappingName?: string;
  rowNumber?: number;
  field?: string;
  value?: string;
  expectedFormat?: string;
  httpStatus?: number;
  code?: string;
  traceId?: string;
  requestId?: string;
  reference?: string;
  contentType?: string;
  retryable?: boolean;
  guidance?: string;
}

export interface FileProcessingTestCall {
  id: string;
  label: string;
  status: "processing" | "completed" | "failed";
  rowCount?: number;
  mappedInput?: unknown;
  outcome?: unknown;
  error?: string;
  errorDetails?: FileProcessingTestErrorDetail;
}

export interface FileProcessingTestResourceReference {
  scope: FileProcessingScope;
  id: string;
  action: "created" | "updated" | "upserted";
}

export interface FileProcessingTestResourceDetail {
  reference: FileProcessingTestResourceReference;
  data: unknown;
}

export interface FileProcessingTestRun {
  schemaVersion: 1;
  id: string;
  projectId: string;
  releaseId: string;
  scope: FileProcessingScope;
  fileName: string;
  path: string;
  status: FileProcessingTestStatus;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  milestones: FileProcessingTestMilestone[];
  calls: FileProcessingTestCall[];
  affectedResources?: FileProcessingTestResourceReference[];
  affectedResourcesTruncated?: boolean;
  failedRowCount?: number;
  failedRows?: { rowNumber: number; error: string }[];
  summary?: unknown;
  error?: string;
  errorDetails?: FileProcessingTestErrorDetail;
  retentionAvailable?: boolean;
  emitEvents?: boolean;
}

export interface FileProcessingTestUnavailable {
  id: string;
  retentionAvailable: false;
}

export type FileProcessingTestLookupResponse = FileProcessingTestRun | FileProcessingTestUnavailable;

export interface StartFileProcessingTestInput {
  releaseId: string;
  scope: FileProcessingScope;
  fileName: string;
  content: string;
  emitEvents?: boolean;
}

export interface StartFileProcessingTestResponse {
  run: FileProcessingTestRun;
  jobId: string;
}

export interface ReleaseMapping {
  id: string;
  name: string;
  type: "request" | "response" | "contact" | "entities" | "orgmodels" | "delta" | "custom";
}

export interface ReleaseFileProcessingMappingRule {
  id: string;
  mapping: string;
  isDefault?: boolean;
  match?: string;
  index?: string[];
  contactData?: {
    enabled?: boolean;
    contactColumn?: string;
    path?: string;
    mapping?: string;
    externalIdentifier?: boolean;
    externalIdentifierNamespace?: string;
    externalIdentifierKey?: string;
  };
  batching?: {
    mode?: "csv-group-by";
    groupBy?: string[];
    maxConcurrency?: number;
    delayMs?: number;
    failurePolicy?: "fail-partition";
  };
}

export interface ReleaseFileProcessingScope {
  mapping?: ReleaseFileProcessingMappingRule[];
  sessionGoal?: string | null;
  [key: string]: unknown;
}

export interface ReleaseFileProcessing {
  extensions?: ReleaseFileProcessingScope | ReleaseFileProcessingMappingRule[];
  sessions?: ReleaseFileProcessingScope | ReleaseFileProcessingMappingRule[];
  contacts?: ReleaseFileProcessingScope | ReleaseFileProcessingMappingRule[];
  entities?: ReleaseFileProcessingScope | ReleaseFileProcessingMappingRule[];
  orgmodels?: ReleaseFileProcessingScope | ReleaseFileProcessingMappingRule[];
  orgModels?: ReleaseFileProcessingScope | ReleaseFileProcessingMappingRule[];
}

export interface ReleaseFileProcessingMapping {
  sessions?: ReleaseFileProcessingMappingRule[];
  contacts?: ReleaseFileProcessingMappingRule[];
  entities?: ReleaseFileProcessingMappingRule[];
  orgmodels?: ReleaseFileProcessingMappingRule[];
  orgModels?: ReleaseFileProcessingMappingRule[];
}

export interface ReleaseConnection {
  id: string;
  name: string;
  url: string;
}

export interface FileAttachment {
  name: string;
  label?: string;
  fileType?: string;
  data?: string;
  reference?: string;
}

// export interface Documents {
//   [key: string]: Documents | ReleaseDocument;
// }

export * as ReleaseTestCasesNS from "./releaseTestCases";

// --

export interface ModelInput {
  description: string;
  publicId?: string;
  entity: string; // you can group inputs under their entity
  enum?: string; // just the name of the enum
  hidden: boolean;
  id: string; // uuid
  type: ControlType;
  usedIn: string[]; // source name(s) this input comes from

  graph_type?: "derived" | "goal" | "input";

  // scope : string;
  // rows  : any[];
}

// --

export interface EntityDescription {
  text: string;
  id: string;
  name: string;
  pathToGlobal: string[];
  pathToLeaf: string[];
}

export type RuleDocContentsV1 = any[];

export interface RuleDocContentsV2 {
  rules: any[];
  source: any[];
  sections: any[];
}

export type RuleDocContentsV3 = RuleDocContentsV2;

export interface RuleSheet {
  title: string;
  headings: { type: string; legend: string }[];
  rows: { type: string; text: string }[][];
  //columns: any[];
  //data: any[];
}

export interface RuleSheetContents {
  declarations: any[];
  sheets: RuleSheet[];
}

export interface DataTableContents {
  headers: string[];
  identifier?: string;
  rows: Array<string | number>[];
}

interface BaseDocument<T, C> {
  name: string;
  type: T;
  errors: DocumentError[];
  created: string;
  /** New value, exists on new documents only */
  createdBy?: string;
  /** New value, exists on new documents only */
  createdByName?: string;
  release: string;
  version?: number;
  contents: C;
  lastCompiledContents: C | undefined;
  lastModified: string;
  /** New value, exists on new documents only */
  updatedBy?: string;
  /** New value, exists on new documents only */
  updatedByName?: string;
  id: string;
  graph: RawRuleGraph;
  lastCompiled?: number;
  sentForCompile?: number;
  metadata: Record<string, string>;
  vectors?: number[][];
}

export interface DocumentError {
  type?: string;
  message?: string;
}

export type RuleDocDocumentV1 = BaseDocument<"ruledoc", RuleDocContentsV1> & {
  version: undefined | 1;
};

// TODO we should rename this or come up with a different method, i dont like have the version number in the type name
export type RuleDocDocumentV2 = BaseDocument<"ruledoc", RuleDocContentsV2> & {
  version: 2;
};

export type RuleDocDocumentV3 = BaseDocument<"ruledoc", RuleDocContentsV3> & {
  version: 3;
};

export type RuleSheetDocument = BaseDocument<"rulesheet", RuleSheetContents>;
export type DataTableDocument = BaseDocument<"datatable", DataTableContents>;
export type MappingDocument = BaseDocument<"mapping", ReleaseMapping | ReleaseMapping[]>;
export type ConnectionDocument = BaseDocument<"connection", ReleaseConnection | ReleaseConnection[]>;
export type DerivedTableDocument = BaseDocument<"derivedtable", DerivedTable>;
export type RuleDocDocument = RuleDocDocumentV1 | RuleDocDocumentV2 | RuleDocDocumentV3;

export type Document =
  | RuleDocDocument
  | RuleSheetDocument
  | DataTableDocument
  | MappingDocument
  | ConnectionDocument
  | DerivedTableDocument;
export type DocumentContents = Document["contents"];
export type DocumentType = Document["type"];

export const TaskStatusEnum = {
  NotStarted: "not-started",
  InProgress: "in-progress",
  Review: "review",
  Approved: "approved",
  Closed: "closed",
  Rejected: "rejected",
  Blocked: "blocked",
} as const;
export type TaskStatus = (typeof TaskStatusEnum)[keyof typeof TaskStatusEnum];

export const TaskPriorityEnum = {
  low: "low",
  medium: "medium",
  high: "high",
  urgent: "urgent",
} as const;
export type TaskPriority = (typeof TaskPriorityEnum)[keyof typeof TaskPriorityEnum];

export interface Task {
  id: string;
  type: string; // TODO should this be restricted to "rule" | "flow"?
  title: string;
  description: any[];
  status: TaskStatus;
  priority: TaskPriority;
  tags: string[]; // tags are arbitrary strings and will not be synced
  // sprintPoints:       number; // Nope, this isnt clickup
  workspace?: string;
  model?: string;
  release?: string;
  document?: string;
  section?: string;
  assignees: string[];
  assigneesData: AssigneesData[];
  dueDate: string;
  created: string;
  lastModified: string;
  createdBy: string;
  createdByName: string;
  lastModifiedBy: string;
  lastModifiedByName: string;
  comments: Comment[];
  interaction?: any; // TODO what is this data?
  escalation?: any; // TODO what is this data?
}

export interface NotificationTask extends Omit<Task, "description" | "priority" | "tags" | "comments" | "created" | "lastModified" | "dueDate"> {
  type: "notification";
  subject: string;
  body: string;
  notificationStatus: "unread" | "read" | "archived";
  action?: { path: string; label?: string };
  severity: "info" | "warning" | "critical";
  thread: string;
  created: string | number;
  lastModified: string | number;
  dueDate?: string;
  description?: any[];
  priority?: TaskPriority;
  tags?: string[];
  comments?: Comment[];
  session?: string;
  interaction?: string;
  attribute?: string;
  indicesValues?: Record<string, unknown>;
  context?: Record<string, unknown>;
}

export interface AssigneesData {
  id: string;
  identity_id: string;
  firstName: string;
  lastName: string;
  // combined name
  name: string;
  // FIXME - why can we have name or full_name, we should standardise on one of these
  full_name?: string;
  email: string;
  avatar?: string;
}

export interface Comment {
  id: string;
  text: any[];
  created: string;
  lastModified: string;
  createdBy: string;
  createdByName: string;
  lastModifiedBy: string;
  lastModifiedByName: string;
}

export interface Workspace {
  id: string;
  name: string;
  visibility: "public" | "private";
  users: any[];
  usersData: AssigneesData[];
  created: string;
  lastModified: string;
  canaryTestingWorkspace?: boolean;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  created: string;
  lastModified: string;
  workspace: string;
  // readonly - based on workspace id
  workspace_name: string;
  tags: string[];
  visibility: "public" | "private";
  locale: string;
  locked?: boolean;
  /** See {@link Model.disableGuids}. Set at creation only. */
  disableGuids?: boolean;
}

export const iteratePlateElements = (elements: any, callback: (el: any) => undefined | false): undefined | false => {
  for (const el of elements) {
    if (callback(el) === false) {
      return false;
    }

    if (el.children) {
      if (iteratePlateElements(el.children, callback) === false) {
        return false;
      }
    }
  }
};

export const findInterviewStep = (
  steps: InterviewStep[] | undefined,
  predicate: (node: InterviewStep) => boolean,
  parent?: InterviewStep,
): {
  step: InterviewStep | undefined;
  parent: InterviewStep | undefined;
  index: number;
} => {
  let index = -1;
  if (steps) {
    for (const step of steps) {
      index++;
      if (predicate(step)) {
        return { step: step, parent, index };
      }

      if (step.steps) {
        const childrenResult = findInterviewStep(step.steps, predicate, step);
        if (childrenResult.step) {
          return childrenResult;
        }
      }
    }
  }

  return { step: undefined, parent: undefined, index: -1 };
};

// In azure table names are limited to 63 chars, and the two guids will be 36 chars each. So we have to limit and hope that we don't get a conflict (very very unlikely)
export const getDataTableName = (modelId: string, tableRef: string) =>
  `t${(modelId.toString().replace(/[^a-zA-Z0-9]/g, "") + tableRef.replace(/[^a-zA-Z0-9]/g, "")).substring(0, 62)}`;

export const findContainmentRelationship = (
  relationships: Relationship[],
  relationshipId: string,
): Relationship | undefined => {
  let relationship = relationships.find((relationship) => relationship.id === relationshipId);
  if (!relationship) {
    return undefined;
  }

  while (relationship) {
    if (relationship.containment) {
      return relationship;
    }
    relationship = relationships.find((other) => other.containment && other.target === relationship?.target);
    if (!relationship) {
      return undefined;
    }
  }
  return undefined;
};

export * from "./CsvDateFormats";

export * from "./EventConditions";
